<template>
  <main class="bg-about-background bg-top pb-20 bg-[length:130rem] h-full bg-no-repeat bg-white pt-28">
    <section class="mx-auto max-w-4xl">
      <div class="mb-6 flex flex-col gap-2">
        <div class="flex items-center gap-2 text-sm font-medium text-gray-500">
          <CalendarDaysIcon class="h-4 w-4 text-primary" />
          Events
        </div>
        <h2 class="text-gray-900 text-3xl font-semibold">
          Upcoming Events
        </h2>
        <p class="text-gray-600">
          Reserve your seat at curated Nevcap gatherings.
        </p>
      </div>
      <div
        v-if="loading"
        class="text-sm text-gray-500"
      >
        Loading events...
      </div>
      <div
        v-else-if="events.length === 0"
        class="text-sm text-gray-500"
      >
        No events scheduled yet.
      </div>
      <div
        v-else
        class="grid gap-5"
      >
        <div
          v-for="event in events"
          :key="event._id"
          class="bg-white border border-gray-200 shadow-sm rounded-2xl p-6"
        >
          <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div class="space-y-2">
              <router-link
                class="text-gray-900 text-lg font-semibold hover:text-primary"
                :to="`/dashboard/events/${event._id}`"
              >
                {{ event.title }}
              </router-link>
              <div class="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1">
                  <CalendarDaysIcon class="h-3.5 w-3.5" />
                  {{ formatDate(event.date) }}
                </span>
                <span
                  v-if="event.startTime || event.endTime"
                  class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1"
                >
                  <ClockIcon class="h-3.5 w-3.5" />
                  {{ formatTimeRange(event.startTime, event.endTime) }}
                </span>
                <span
                  v-if="event.location"
                  class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1"
                >
                  <MapPinIcon class="h-3.5 w-3.5" />
                  {{ event.location }}
                </span>
                <span
                  v-if="event.type"
                  class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1"
                >
                  {{ event.type }}
                </span>
              </div>
              <div class="text-sm font-semibold text-gray-900">
                <span v-if="event.hidePrice">Paid event</span>
                <span v-else-if="typeof event.price === 'number'">£{{ formatPrice(event.price) }}</span>
                <span v-else>Free</span>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <router-link
                class="inline-flex items-center rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
                :to="`/dashboard/events/${event._id}`"
              >
                View
                <ArrowRightIcon class="ml-2 h-4 w-4" />
              </router-link>
              <a
                v-if="event.ticketUrl"
                class="inline-flex items-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-[#c9ad99]"
                :href="event.ticketUrl"
                rel="noreferrer"
                target="_blank"
              >
                <TicketIcon class="mr-2 h-4 w-4" />
                Get ticket
              </a>
            </div>
          </div>
          <p
            v-if="event.description"
            class="text-sm text-gray-600 mt-4"
          >
            {{ event.description }}
          </p>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import {defineComponent, inject, onMounted, ref, watchEffect} from 'vue';
import axios from 'axios';
import {Event} from '@/types';
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  TicketIcon,
} from "@heroicons/vue/24/outline";

export default defineComponent({
  name: 'Events',
  components: {
    ArrowRightIcon,
    CalendarDaysIcon,
    ClockIcon,
    MapPinIcon,
    TicketIcon,
  },
  setup() {
    const {settings} = inject(settingsKey)!;
    const events = ref<Event[]>([]);
    const loading = ref(false);

    const fetchEvents = async (): Promise<void> => {
      loading.value = true;
      try {
        const response = await axios.get<Event[]>('/events');
        events.value = response.data;
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (value?: string): string => {
      if (!value) return 'Unknown date';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Unknown date';
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(date);
    };

    const formatTimeRange = (start?: string, end?: string): string => {
      if (start && end) return `${start} to ${end}`;
      if (start) return `From ${start}`;
      if (end) return `Until ${end}`;
      return '';
    };

    const formatPrice = (value: number): string => {
      return value.toFixed(2);
    };

    onMounted(async () => {
      await fetchEvents();
    });

    watchEffect(() => {
      if (settings.value) {
        useHead({
          title: 'Events',
          meta: [
            {name: 'description', content: '...'},
            {name: 'keywords', content: '...'},
            {name: 'author', content: '...'},
          ],
        });
      }
    });

    return {
      events,
      loading,
      formatDate,
      formatTimeRange,
      formatPrice,
    };
  },
});
</script>
